import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Code2, Copy, Check, Globe, Paintbrush } from "lucide-react";
import { useToast } from "@/shared/components/ui/use-toast";
import { ScrollArea } from "@/shared/components/ui/scroll-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/shared/components/ui/sheet";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs";

interface EmbedChatFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  workspaceName: string;
  onSuccess?: () => void;
}

export function EmbedChatFlyout({ isOpen, onClose, workspaceId, workspaceName }: EmbedChatFlyoutProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [copiedField, setCopiedField] = useState<string | null>(null);

  // Widget customization
  const [widgetTitle, setWidgetTitle] = useState(workspaceName);
  const [widgetHeight, setWidgetHeight] = useState("600");
  const [widgetWidth, setWidgetWidth] = useState("400");
  const [widgetTheme, setWidgetTheme] = useState<"light" | "dark">("light");

  const baseUrl = window.location.origin;
  const widgetUrl = `${baseUrl}/widget/chat/${workspaceId}?theme=${widgetTheme}&title=${encodeURIComponent(widgetTitle)}`;

  const iframeCode = `<iframe
  src="${widgetUrl}"
  width="${widgetWidth}"
  height="${widgetHeight}"
  frameborder="0"
  allow="clipboard-write"
  style="border: 1px solid #e5e7eb; border-radius: 8px;"
></iframe>`;

  const scriptCode = `<script>
  (function() {
    var d = document, s = d.createElement('script');
    s.src = '${baseUrl}/widget/chat-loader.js';
    s.setAttribute('data-workspace-id', '${workspaceId}');
    s.setAttribute('data-theme', '${widgetTheme}');
    s.setAttribute('data-title', '${widgetTitle}');
    s.async = true;
    d.body.appendChild(s);
  })();
</script>`;

  const copyToClipboard = async (text: string, field: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast({
        title: t("playground.embedWidget.codeCopied"),
        description: t("playground.embedWidget.codeCopiedDescription"),
      });
      setTimeout(() => setCopiedField(null), 2000);
    } catch {
      toast({
        title: t("playground.embedWidget.copyFailed"),
        variant: "destructive",
      });
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Code2 className="h-5 w-5" />
            {t("playground.embedWidget.title")}
          </SheetTitle>
          <p className="text-sm text-muted-foreground mt-1">
            {t("playground.embedWidget.subtitle")} <span className="font-medium">{workspaceName}</span>
          </p>
        </SheetHeader>

        <ScrollArea className="mt-4 h-[calc(100vh-12rem)]">
          <div className="space-y-6">
            {/* Customization */}
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Paintbrush className="h-4 w-4" />
                {t("playground.embedWidget.customize")}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="widget-title">{t("playground.embedWidget.widgetTitle")}</Label>
                  <Input
                    id="widget-title"
                    value={widgetTitle}
                    onChange={(e) => setWidgetTitle(e.target.value)}
                    placeholder={workspaceName}
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="widget-width">{t("playground.embedWidget.width")}</Label>
                  <Input
                    id="widget-width"
                    value={widgetWidth}
                    onChange={(e) => setWidgetWidth(e.target.value)}
                    placeholder="400"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="widget-height">{t("playground.embedWidget.height")}</Label>
                  <Input
                    id="widget-height"
                    value={widgetHeight}
                    onChange={(e) => setWidgetHeight(e.target.value)}
                    placeholder="600"
                  />
                </div>

                <div className="col-span-2 space-y-1.5">
                  <Label>{t("playground.embedWidget.theme")}</Label>
                  <div className="flex gap-2">
                    <Button
                      variant={widgetTheme === "light" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWidgetTheme("light")}
                      className="flex-1"
                    >
                      {t("playground.embedWidget.themeLight")}
                    </Button>
                    <Button
                      variant={widgetTheme === "dark" ? "default" : "outline"}
                      size="sm"
                      onClick={() => setWidgetTheme("dark")}
                      className="flex-1"
                    >
                      {t("playground.embedWidget.themeDark")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            {/* Embed Code */}
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium">
                <Globe className="h-4 w-4" />
                {t("playground.embedWidget.embedCode")}
              </div>

              <Tabs defaultValue="iframe">
                <TabsList className="w-full">
                  <TabsTrigger value="iframe" className="flex-1">iFrame</TabsTrigger>
                  <TabsTrigger value="script" className="flex-1">Script</TabsTrigger>
                </TabsList>

                <TabsContent value="iframe" className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t("playground.embedWidget.iframeDescription")}
                  </p>
                  <div className="relative">
                    <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
                      {iframeCode}
                    </pre>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-7 w-7"
                      onClick={() => copyToClipboard(iframeCode, "iframe")}
                    >
                      {copiedField === "iframe" ? (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </TabsContent>

                <TabsContent value="script" className="space-y-2">
                  <p className="text-xs text-muted-foreground">
                    {t("playground.embedWidget.scriptDescription")}
                  </p>
                  <div className="relative">
                    <pre className="bg-muted p-3 rounded-md text-xs overflow-x-auto whitespace-pre-wrap break-all font-mono">
                      {scriptCode}
                    </pre>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="absolute top-2 right-2 h-7 w-7"
                      onClick={() => copyToClipboard(scriptCode, "script")}
                    >
                      {copiedField === "script" ? (
                        <Check className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Copy className="h-3.5 w-3.5" />
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            {/* Direct URL */}
            <div className="space-y-2">
              <Label>{t("playground.embedWidget.directUrl")}</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={widgetUrl}
                  className="text-xs font-mono"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={() => copyToClipboard(widgetUrl, "url")}
                >
                  {copiedField === "url" ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
